import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { TIME_FORMAT } from '../constants/time';

const { DATE_AND_TIME, HOUR_12 } = TIME_FORMAT;

export const DAY_PERIOD = {
  MORNING: 'morning',
  AFTERNOON: 'afternoon',
  EVENING: 'evening',
};

export const DAY_PERIODS = [
  { key: DAY_PERIOD.MORNING, label: 'Morning' },
  { key: DAY_PERIOD.AFTERNOON, label: 'Afternoon' },
  { key: DAY_PERIOD.EVENING, label: 'Evening' },
];

// As in pbt-ui: before 12:00 is morning, before 18:00 afternoon, then evening.
// `time` is a 24-hour "HH:mm".
export const getDayPeriod = (time = '') => {
  const hour = Number(time.slice(0, 2));
  if (hour < 12) return DAY_PERIOD.MORNING;
  if (hour < 18) return DAY_PERIOD.AFTERNOON;
  return DAY_PERIOD.EVENING;
};

// A slot's `value` is "YYYYMMDD HH:mm"; its `label` is the 12-hour time shown.
export const getSlotPeriod = ({ value = '' }) =>
  getDayPeriod(value.split(' ')[1]);

// A day's timeslots grouped by part of the day, keeping only the parts that have slots.
export const groupTimeslotsByPeriod = (timeslots = []) =>
  DAY_PERIODS.map(({ key, label }) => ({
    key,
    label,
    timeslots: timeslots.filter((slot) => getSlotPeriod(slot) === key),
  })).filter((group) => group.timeslots.length > 0);

/**
 * Start times a patient can pick on one date: each free range from the availability API is
 * cut into back-to-back slots of the appointment length, dropping a slot that would overrun.
 * Shared by self-serve booking and reschedule so both offer exactly the same times.
 */
export const buildTimeslotOptions = ({
  bookingDate,
  appointmentLength,
  availableTimeList = [],
  timezone,
}) => {
  if (!bookingDate || !appointmentLength) return [];

  const availableTime = availableTimeList.find(
    ({ date }) => date === bookingDate
  );
  if (!availableTime) return [];

  // The free ranges are wall-clock times in the booking's zone; reading them in the phone's
  // zone would skip or repeat slots on a daylight-saving change the booking zone doesn't have.
  const parseInZone = (value) =>
    timezone
      ? momentTimezone.tz(value, DATE_AND_TIME, timezone)
      : moment(value, DATE_AND_TIME);
  const slots = [];

  availableTime.availableTimeslots.forEach(({ start, end }) => {
    const endMoment = parseInZone(`${bookingDate} ${end}`);
    let currentMoment = parseInZone(`${bookingDate} ${start}`);

    while (currentMoment.isBefore(endMoment)) {
      const endOfThisSlot = currentMoment
        .clone()
        .add(appointmentLength, 'minute');

      if (endOfThisSlot.isSameOrBefore(endMoment)) {
        slots.push({
          value: currentMoment.format(DATE_AND_TIME),
          label: currentMoment.format(HOUR_12),
        });
      }

      currentMoment = endOfThisSlot;
    }
  });

  return slots;
};
